import { TxnType, formatAmount, isFiatCurrency, AuditTxn, fiatCurrencyCodes } from "@moonscape/shared";

import { parseDateTime, formatInTaxTimezone } from "./format";

// Type definition for exchange object
interface Exchange {
  api_active: boolean;
  api_required_fields: string[];
  csv_active: boolean;
  exchangename: string;
  id: string;
  image_url: string;
  popular: boolean;
  rank: number;
  tag: string;
  thumbnail_url: string;
  website_url: string | null;
}

export class AuditTradeItemClass {
  // class property types

  tradeid: string;
  type: string;
  exchange: Exchange | string;
  timestamp: string;
  datetime: string;
  buyasset: string;
  buyqty: string;
  sellasset: string;
  sellqty: string;
  feeqty: string;
  feeasset: string;
  wallet: string;
  side: string;
  userId: string;
  // added properties for audit
  buyvalue: number;
  sellvalue: number;
  feevalue: number;
  price: string;
  importtype: string;
  ccxt_id: string;
  sellvalueasset: string;
  buyvalueasset: string;
  created_at: string;
  updated_at: string;
  raw_exchange_txn_id: string;
  ext_txn_id: string;
  txn_id: string;
  value: number;
  valuerate: number;
  valueasset: string;
  valuenote: string;
  final_valuenote: string;
  feevaluerate: number;
  feevaluenote: string;
  final_value: number;
  final_valuerate: number;
  final_feevalue: number;
  final_feevaluerate: number;
  final_valueasset: string;

  deleted: boolean;
  txn_note: string;
  spam_category: string;

  category: string;

  // coingeckoIds
  buyCoinGeckoId: string;
  sellCoinGeckoId: string;

  // icon URLs from API
  buyIconUrl: string;
  sellIconUrl: string;
  exchangeIconUrl: string;

  // API key label for exchange account identification
  apikey_label: string;

  // asset details support in blockchain
  buyAssetDetails: object;
  sellAssetDetails: object;

  // columns denoting missing fields
  missing_final_feevalue: boolean;
  missing_final_value: boolean;

  // audit fields
  errorMsg?: string;
  errorType?: string;
  error_context?: {
    suggested_resolution?: string;
    asset?: string;
    ledger?: any[];
    balance_before_txn?: number;
    missing_fields?: string[];
    [key: string]: any;
  };

  // raw exchange transaction fields
  raw_data?: object;
  raw_source_txn_id?: string;
  raw_imported_at?: string;
  raw_exchange?: string;

  constructor(tradeItem) {
    // Type guard to check if txn is an AuditTxn
    const isAuditTxn = (txn: any): txn is AuditTxn => {
      return (txn as AuditTxn)?.error_data !== undefined;
    };

    let txnItem = tradeItem;

    if (isAuditTxn(tradeItem)) {
      txnItem = tradeItem.error_data;
      this.errorMsg = tradeItem?.error_msg;
      this.errorType = tradeItem?.error_type;
      this.error_context = tradeItem?.error_context;
    }

    this.tradeid = txnItem?.tradeid;
    this.type = txnItem?.type;
    this.exchange = txnItem?.exchange;
    this.timestamp = txnItem?.timestamp;
    this.datetime = txnItem?.datetime;
    this.buyasset = txnItem?.buyasset;
    this.buyqty = txnItem?.buyqty;
    this.sellasset = txnItem?.sellasset;
    this.sellqty = txnItem?.sellqty;
    this.feeqty = txnItem?.feeqty;
    this.feeasset = txnItem?.feeasset;
    this.wallet = txnItem?.wallet;
    this.side = txnItem?.side;
    this.userId = txnItem?.userId;

    // added properties
    this.buyvalue = txnItem?.buyvalue;
    this.sellvalue = txnItem?.sellvalue;
    this.feevalue = txnItem?.feevalue;
    this.price = txnItem?.price;
    this.importtype = txnItem?.importtype;
    this.ccxt_id = txnItem?.ccxt_id;
    this.sellvalueasset = txnItem?.sellvalueasset;
    this.buyvalueasset = txnItem?.buyvalueasset;
    this.created_at = txnItem?.created_at;
    this.updated_at = txnItem?.updated_at;
    this.raw_exchange_txn_id = txnItem?.raw_exchange_txn_id;
    this.ext_txn_id = txnItem?.ext_txn_id;
    this.txn_id = txnItem?.txn_id;
    this.value = txnItem?.value;
    this.valuerate = txnItem?.valuerate;
    this.valueasset = txnItem?.valueasset;
    this.valuenote = txnItem?.valuenote;
    this.feevaluerate = txnItem?.feevaluerate;
    this.feevaluenote = txnItem?.feevaluenote;
    this.final_value = txnItem?.final_value;
    this.final_valuerate = txnItem?.final_valuerate;
    this.final_valuenote = txnItem?.final_valuenote;
    this.final_feevalue = txnItem?.final_feevalue;
    this.final_feevaluerate = txnItem?.final_feevaluerate;
    this.final_valueasset = txnItem?.final_valueasset;
    // columns denoting missing fields
    this.missing_final_feevalue = txnItem?.missing_final_feevalue;
    // A pure fiat movement (single fiat leg — deposits, withdrawals, funding)
    // carries no engine price by design: its worth IS its amount. The server's
    // flag is a bare `value IS NULL`, which painted fiat deposits as
    // "Still calculating" forever. Same rule as iOS NewIALedgerRow.
    const buyFiat = fiatCurrencyCodes.includes(txnItem?.buyasset);
    const sellFiat = fiatCurrencyCodes.includes(txnItem?.sellasset);
    const pureFiat =
      (buyFiat && !txnItem?.sellasset) || (sellFiat && !txnItem?.buyasset);
    this.missing_final_value = txnItem?.missing_final_value && !pureFiat;

    // coingeckoIds
    this.buyCoinGeckoId = txnItem?.buyCoinGeckoId;
    this.sellCoinGeckoId = txnItem?.sellCoinGeckoId;

    // icon URLs
    this.buyIconUrl = txnItem?.buyIconUrl;
    this.sellIconUrl = txnItem?.sellIconUrl;
    this.exchangeIconUrl = txnItem?.exchangeIconUrl;

    // API key label
    this.apikey_label = txnItem?.apikey_label;

    // deleted
    this.deleted = txnItem?.deleted;
    this.txn_note = txnItem?.txn_note;
    this.spam_category = txnItem?.spam_category;

    // raw exchange transaction fields
    this.raw_data = txnItem?.raw_data;
    this.raw_source_txn_id = txnItem?.raw_source_txn_id;
    this.raw_imported_at = txnItem?.raw_imported_at;
    this.raw_exchange = txnItem?.raw_exchange;

    // Categorize the transaction during initialization - at the end of the constructor
    this.category = "unknownType"; // default value
    this.categorizeTxn();
  }
  // Categorize the transaction type
  categorizeTxn() {
    if (this.isTradeType()) {
      this.category = "tradeType";
    } else if (this.isGiftType()) {
      this.category = "giftType";
    } else if (this.isTransferType()) {
      this.category = "transferType";
    } else if (this.isBlockchainTransferType()) {
      this.category = "blockchainTransferType";
    } else if (this.isInterestType()) {
      this.category = "interestType";
    } else {
      this.category = "unknownType";
    }
  }

  getErrorMsg() {
    console.log("error_msg", this.errorMsg);
    return this.errorMsg ?? "";
  }

  getCcy1() {
    if (!this.isTradeType()) {
      return this.getAsset() ?? "Unknown";
    }
    return this.getAssetPair().split("/")?.[0] ?? this.getAsset() ?? "Unknown";
  }
  getCoingeckoIdCcy1() {
    if (!this.isTradeType()) {
      return this.buyCoinGeckoId ?? this.sellCoinGeckoId ?? this.getAsset();
    }
    return this.getCcy1() === this.buyasset
      ? this.buyCoinGeckoId ?? this.buyasset
      : this.sellCoinGeckoId ?? this.sellasset;
  }
  getCoingeckoIdCcy2() {
    if (!this.isTradeType()) {
      return this.buyCoinGeckoId ?? this.sellCoinGeckoId ?? this.getAsset();
    }
    return this.getCcy2() === this.sellasset
      ? this.sellCoinGeckoId ?? this.sellasset
      : this.buyCoinGeckoId ?? this.buyasset;
  }

  getCcy2() {
    if (!this.isTradeType()) {
      return this.getAsset() ?? "Unknown";
    }
    return this.getAssetPair().split("/")?.[1] ?? this.getAsset() ?? "Unknown";
  }

  getExchangeName() {
    if (typeof this.exchange === "string") {
      switch (this.exchange) {
        case "coinbase":
          return "Coinbase";
        case "binance":
          return "Binance";
        case "kraken":
          return "Kraken";
        case "cryptocom":
          return "Crypto.com";
        default:
          return this.exchange;
      }
    }

    if (this.exchange && "exchangename" in this.exchange) {
      return this.exchange.exchangename;
    }

    return "Unknown Exchange";
  }

  /**
   * Get formatted exchange name with optional label
   * Format: "Label (Exchange)" if label exists, otherwise just "Exchange"
   */
  getFormattedExchangeName(): string {
    const exchangeName = this.getExchangeName();

    if (this.apikey_label && this.apikey_label.trim()) {
      return `${this.apikey_label} (${exchangeName})`;
    }

    return exchangeName;
  }

  // getFormattedSide() {
  //   if (this.side === "buy") return "Buy";
  //   if (this.side === "sell") return "Sell";
  // }

  getSideColor() {
    if (this.isTransferType()) return "grey";
    return `${this.side === "buy" ? "green" : "red"}`;
  }

  getTransferColor() {
    if (this.type === TxnType.DEPOSIT) return "green";
    if (this.type === TxnType.WITHDRAWAL) return "blue";
    if (this.type === TxnType.INTEREST) return "indigo";
  }

  // New helper function to parse datetime consistently
  private parseDateTime(datetime: string) {
    return parseDateTime(datetime);
  }

  formattedTimestamp(taxTimezone?: string | null) {
    if (!this.datetime) return "-";
    return formatInTaxTimezone(this.datetime, taxTimezone ?? null, "MMM D YYYY, HH:mm:ss");
  }

  formattedTime() {
    const date = this.parseDateTime(this.datetime);
    if (!date?.isValid()) return "-";
    return date.format("HH:mm:ss");
  }

  formattedDate(taxTimezone?: string | null) {
    if (!this.datetime) return "-";
    return formatInTaxTimezone(this.datetime, taxTimezone ?? null, "MMM D YYYY");
  }

  /** quantity in its asset's convention: fiat legs 2dp, crypto legs crypto precision */
  fmtQty(qty, asset) {
    return formatAmount(qty, undefined, isFiatCurrency(asset));
  }

  /**
   * get amount - should handle transfers and interest
   * @returns formattedAmount
   */
  getFormattedAmount() {
    if (this.isTradeType()) {
      if (this.side === "sell") {
        return this.sellqty !== null ? `-${this.fmtQty(this.sellqty, this.sellasset)}` : "-";
      } else {
        return this.buyqty !== null ? `+${this.fmtQty(this.buyqty, this.buyasset)}` : "-";
      }
    } else if (this.isTransferType()) {
      if (this.type === TxnType.DEPOSIT) {
        return this.buyqty !== null ? `+${this.fmtQty(this.buyqty, this.buyasset)}` : "-";
      } else if (this.type === TxnType.WITHDRAWAL) {
        // return this.sellqty !== null ? `-${formatAmount(this.sellqty)}` : "-";
      }
    } else if (this.isInterestType()) {
      return this.buyqty !== null ? `+${this.fmtQty(this.buyqty, this.buyasset)}` : "-";
    }
  }

  getFormattedAmountWithCurrency() {
    return `${this.getFormattedAmount()} ${this.getAsset()}`;
  }

  getCounterAmount() {
    if (this.isTradeType()) {
      if (this.side === "sell") {
        return this.buyqty !== null ? `+${this.fmtQty(this.buyqty, this.buyasset)}` : "-";
      } else {
        return this.sellqty !== null ? `-${this.fmtQty(this.sellqty, this.sellasset)}` : "-";
      }
    } else if (this.isTransferType()) {
      if (this.type === TxnType.DEPOSIT) {
        // return this.buyqty !== null ? `+${formatAmount(this.buyqty)}` : "-";
      } else if (this.type === TxnType.WITHDRAWAL) {
        return this.sellqty !== null ? `-${this.fmtQty(this.sellqty, this.sellasset)}` : "-";
      }
    }
  }

  getAsset() {
    if (this.type === TxnType.GIFT_RECEIVED) {
      return this.buyasset ?? "Unknown";
    }

    if (this.type === TxnType.GIFT_SENT || this.type === TxnType.DONATION) {
      return this.sellasset ?? "Unknown";
    }

    if (this.isInterestType() || this.type === TxnType.DEPOSIT) {
      return this.buyasset ?? "Unknown";
    }

    if (this.type === TxnType.WITHDRAWAL) {
      return this.sellasset ?? "Unknown";
    }

    if (this.isTradeType()) {
      return this.side === "buy"
        ? this.buyasset ?? "Unknown"
        : this.sellasset ?? "Unknown";
    }

    return this.buyasset ?? this.sellasset ?? "Unknown";
  }

  getCounterAsset() {
    if (!this.isTradeType()) {
      return null;
    }
    return this.side === "buy"
      ? this.sellasset ?? "Unknown"
      : this.buyasset ?? "Unknown";
  }
  // Methods for categorization
  isTransferType() {
    return [TxnType.DEPOSIT, TxnType.WITHDRAWAL].includes(this.type as TxnType);
  }

  isInterestType() {
    return [TxnType.INTEREST, TxnType.STAKING_REWARD].includes(
      this.type as TxnType
    );
  }

  isBlockchainTransferType() {
    return [TxnType.SEND, TxnType.RECEIVE].includes(this.type as TxnType);
  }

  isGiftType() {
    return [TxnType.GIFT_SENT, TxnType.GIFT_RECEIVED, TxnType.DONATION].includes(this.type as TxnType);
  }

  isTradeType() {
    return [
      TxnType.TRADE,
      TxnType.BUY,
      TxnType.SELL,
      TxnType.ADVANCED_TRADE_FILL,
      TxnType.ADVANCED_TRADE_ORDER,
    ].includes(this.type as TxnType);
  }

  isWithdrawal() {
    return this.type === TxnType.WITHDRAWAL;
  }

  isDeposit() {
    return this.type === TxnType.DEPOSIT;
  }

  isBuy() {
    return this.type === TxnType.BUY;
  }

  isSell() {
    return this.type === TxnType.SELL;
  }
  // Display rules for table columns
  getDisplayData() {
    const baseData = {
      transaction: this.tradeid ? `${this.tradeid.substring(0, 5)}...` : "-",
      type: this.formattedType(),
      side: this.getFormattedSide(),
      buyAsset: this.buyasset ?? "-",
      sellAsset: this.sellasset ?? "-",
      buyAmount: this.buyqty ? `+${this.fmtQty(this.buyqty, this.buyasset)}` : "-",
      sellAmount: this.sellqty ? `-${this.fmtQty(this.sellqty, this.sellasset)}` : "-",
      dateUTC: this.formattedDate(),
      exchangeName: this.getFormattedExchangeName(),
      formattedBuyAmount: this.buyqty
        ? `+${this.fmtQty(this.buyqty, this.buyasset)} ${this.buyasset}`
        : "-",
      formattedSellAmount: this.sellqty
        ? `-${this.fmtQty(this.sellqty, this.sellasset)} ${this.sellasset}`
        : "-",
      /* this will show the asset for either buy or sell or withdrawal */
      asset: this.getAsset(),
      /* shows either Buy Sell, Deposit, Withdrawal */
      formattedType: this.formattedType(),
    };

    // Adjust data based on the category
    switch (this.category) {
      case "tradeType":
        return baseData;
      case "transferType":
        return {
          ...baseData,
          side: "-", // Side not relevant for transfers
        };
      case "blockchainTransferType":
        return {
          ...baseData,
          side: "-", // Side not relevant for transfers
        };
      case "interestType":
        return {
          ...baseData,
          side: "-",
          sellAsset: "-",
          sellAmount: "-",
          formattedSellAmount: "-",
        };
      case "giftType":
        return {
          ...baseData,
          side: "-",
        };
      default:
        return {
          ...baseData,
          type: "Unknown",
          side: "-",
        };
    }
  }

  // Formatting helpers
  formattedType() {
    switch (this.type) {
      case TxnType.WITHDRAWAL:
        return "Withdrawal";
      case TxnType.DEPOSIT:
        return "Deposit";
      case TxnType.BUY:
        return "Buy";
      case TxnType.SELL:
        return "Sell";
      case TxnType.TRADE:
        return "Trade";
      case TxnType.INTEREST:
        return "Interest";
      case TxnType.SEND:
        return "Send";
      case TxnType.RECEIVE:
        return "Receive";
      case TxnType.GIFT_SENT:
        return "Gift Sent";
      case TxnType.GIFT_RECEIVED:
        return "Gift Received";
      case TxnType.DONATION:
        return "Donation";
      default:
        return this.type
          ?.split(" ")
          .map(
            (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
          )
          .join(" ");
    }
  }

  formattedCategory() {
    switch (this.category) {
      case "tradeType":
        return "Trade";
      case "giftType":
        return "Gift";
      case "transferType":
        return "Transfer";
      case "blockchainTransferType":
        return "On-chain Transfer";
      case "interestType":
        return "Interest";
      default:
        return "Unknown";
    }
  }

  getFormattedSide() {
    return this.side === "buy" ? "Buy" : this.side === "sell" ? "Sell" : "-";
  }

  // Individual data accessors
  getTransactionType() {
    return this.formattedType();
  }

  getBuyAmount() {
    return this.buyqty ? `+${this.buyqty}` : "-";
  }

  getSellAmount() {
    return this.sellqty ? `-${this.sellqty}` : "-";
  }

  getDate() {
    return this.formattedDate();
  }

  getSide() {
    return this.getFormattedSide();
  }

  getAssetPair() {
    // For non-trade transactions, just return the relevant asset
    if (!this.isTradeType()) {
      return this.buyasset ?? this.sellasset ?? "Unknown";
    }

    // For trade transactions
    if (this.side === "sell" && this.sellasset && this.buyasset) {
      return `${this.sellasset.toUpperCase()}/${this.buyasset.toUpperCase()}`;
    } else if (this.side === "buy" && this.buyasset && this.sellasset) {
      return `${this.buyasset.toUpperCase()}/${this.sellasset.toUpperCase()}`;
    } else {
      return `${this.buyasset.toUpperCase()}/${this.sellasset.toUpperCase()}`;
    }
  }

  formattedBuyAmountWithCurrency() {
    const amount = this.getFormattedAmount();
    const asset = this.getAsset();
    if (amount && asset) {
      return `${this.getFormattedAmount()} ${this.getAsset()}`;
    } else {
      return "";
    }
  }

  formattedSellAmountWithCurrency() {
    const counterAmount = this.getCounterAmount();
    const counterAsset = this.getCounterAsset();
    // console.log("counterAmount", counterAmount);
    // console.log("counterAsset", counterAsset);
    if (counterAmount && counterAsset) {
      return `${this.getCounterAmount()} ${this.getCounterAsset()}`;
    } else {
      return "";
    }
  }
  formattedFeeQty() {
    return this.fmtQty(this.feeqty, this.feeasset);
  }
  formattedFiatValue() {
    return formatAmount(this.final_value || this.value, undefined, true);
  }

  formattedFiatFeeValue() {
    return formatAmount(this.final_feevalue || this.feevalue, undefined, true);
  }

  getMissingFiatValueProperties() {
    let errorMessage = "";

    const missingValues = [];
    if (this.missing_final_feevalue && this.final_feevalue !== 0) {
      missingValues.push("Fiat fee value");
    }

    // Only check for missing_final_value if isTransferType is not true
    if (
      // !this.isTransferType &&
      this.missing_final_value &&
      this.final_value !== 0
    ) {
      missingValues.push("Fiat transaction value");
    }

    if (missingValues.length > 0) {
      errorMessage = missingValues.join(" AND ") + " is missing.";
    }

    if (errorMessage) {
      return errorMessage;
    } else {
      return false;
    }
  }
  /**
   * this checks the missing_final_value and missing_final_feevalue fields on the SQL that denote
   * if the final_value and final_feevalue are missing
   */
  getMissingKeyProperties() {
    const missingKeys = [];

    if (this.missing_final_feevalue || this.final_feevalue === 0) {
      missingKeys.push("final_feevalue");
    }

    // Only check for missing_final_value if isTransferType is not true
    if (
      !this.isTransferType &&
      this.missing_final_value &&
      this.final_value === 0
    ) {
      missingKeys.push("final_value");
    }

    return missingKeys;
  }

  truncate(input, nchars) {
    if (input.length > nchars) {
      return input.substring(0, nchars) + "...";
    }
    return input;
  }

  /**
   * Get the coin symbol to display for this transaction
   * Handles all transaction types including unsupported ones
   */
  getCoinSymbol(): string {
    // Fee operation (unsupported type)
    if (this.type?.toLowerCase() === 'fee_operation') {
      return (this.feeasset || this.buyasset || this.sellasset || '').toLowerCase();
    }

    // Trade types - prefer the primary asset based on side
    if (this.isTradeType()) {
      return this.side === 'buy'
        ? (this.buyasset || this.sellasset || '').toLowerCase()
        : (this.sellasset || this.buyasset || '').toLowerCase();
    }

    // Send/Receive/Withdrawal - use sell asset
    if (this.isBlockchainTransferType() || this.isWithdrawal()) {
      return (this.sellasset || this.buyasset || '').toLowerCase();
    }

    // Deposit/Interest/Staking - use buy asset
    if (this.isDeposit() || this.isInterestType()) {
      return (this.buyasset || this.sellasset || '').toLowerCase();
    }

    // Fallback: try any available asset
    return (this.buyasset || this.sellasset || this.feeasset || '').toLowerCase();
  }

  /**
   * Get the display name for the asset(s) in this transaction
   * Returns formatted string ready for UI display
   */
  getDisplayAssetName(): string {
    // Fee operation (unsupported type)
    if (this.type?.toLowerCase() === 'fee_operation') {
      return this.feeasset ? `${this.feeasset} Fee` : 'Fee';
    }

    // Trade types - show both assets
    if (this.isTradeType() && this.buyasset && this.sellasset) {
      return `${this.buyasset} / ${this.sellasset}`;
    }

    // All other types - prefer raw transaction data over display data
    // This avoids showing spam/notes that might be in valuenote fields
    const asset = this.buyasset || this.sellasset;
    if (asset) return asset;

    // No buy/sell asset (e.g. zero-value contract interactions like approvals) - fall back to the fee asset
    return this.feeasset ? `${this.feeasset} Fee` : '-';
  }

  /**
   * Get the primary amount to display for this transaction
   * Returns formatted string with asset symbol
   */
  getDisplayAmount(): string {
    // Fee operation (unsupported type)
    if (this.type?.toLowerCase() === 'fee_operation' && this.feeqty && this.feeasset) {
      return `${formatAmount(this.feeqty)} ${this.feeasset}`;
    }

    // Trade types - show buy amount (or sell if buy is missing)
    if (this.isTradeType()) {
      if (this.buyqty && this.buyasset) {
        return `${formatAmount(this.buyqty)} ${this.buyasset}`;
      }
      if (this.sellqty && this.sellasset) {
        return `${formatAmount(this.sellqty)} ${this.sellasset}`;
      }
      return '-';
    }

    // Send/Withdrawal - show sell amount
    if ((this.isBlockchainTransferType() && this.type === TxnType.SEND) || this.isWithdrawal()) {
      if (this.sellqty && this.sellasset) {
        return `${formatAmount(this.sellqty)} ${this.sellasset}`;
      }
    }

    // Receive/Deposit/Interest - show buy amount
    if ((this.isBlockchainTransferType() && this.type === TxnType.RECEIVE) ||
        this.isDeposit() ||
        this.isInterestType()) {
      if (this.buyqty && this.buyasset) {
        return `${formatAmount(this.buyqty)} ${this.buyasset}`;
      }
    }

    // Fallback: try any available amount
    if (this.buyqty && this.buyasset) {
      return `${formatAmount(this.buyqty)} ${this.buyasset}`;
    }
    if (this.sellqty && this.sellasset) {
      return `${formatAmount(this.sellqty)} ${this.sellasset}`;
    }

    // No buy/sell amount (e.g. zero-value contract interactions like approvals) - fall back to the fee
    if (this.feeqty && this.feeasset) {
      return `${formatAmount(this.feeqty)} ${this.feeasset}`;
    }

    return '-';
  }

  /**
   * Get the secondary/counter amount for trade transactions
   * Returns null for non-trade types
   */
  getDisplayCounterAmount(): string | null {
    if (!this.isTradeType()) {
      return null;
    }

    // Show the opposite amount from primary
    if (this.buyqty && this.buyasset && this.sellqty && this.sellasset) {
      // If primary is buy, counter is sell
      return `${formatAmount(this.sellqty)} ${this.sellasset}`;
    }

    return null;
  }

  /**
   * Get the icon URL for the coin to display in this transaction
   * Matches the logic of getCoinSymbol() but returns the corresponding icon URL
   */
  getCoinIconUrl(): string | undefined {
    // Fee operation (unsupported type)
    if (this.type?.toLowerCase() === 'fee_operation') {
      return this.buyIconUrl || this.sellIconUrl;
    }

    // Trade types - prefer the primary asset icon based on side
    if (this.isTradeType()) {
      return this.side === 'buy'
        ? (this.buyIconUrl || this.sellIconUrl)
        : (this.sellIconUrl || this.buyIconUrl);
    }

    // Send/Receive/Withdrawal - use sell asset icon
    if (this.isBlockchainTransferType() || this.isWithdrawal()) {
      return this.sellIconUrl || this.buyIconUrl;
    }

    // Deposit/Interest/Staking - use buy asset icon
    if (this.isDeposit() || this.isInterestType()) {
      return this.buyIconUrl || this.sellIconUrl;
    }

    // Fallback: try any available icon
    return this.buyIconUrl || this.sellIconUrl;
  }

  /**
   * Get the CoinGecko ID for the coin to display in this transaction
   * Matches the logic of getCoinSymbol() but returns the corresponding coingecko ID
   */
  getCoinGeckoId(): string | undefined {
    // Fee operation (unsupported type)
    if (this.type?.toLowerCase() === 'fee_operation') {
      return this.buyCoinGeckoId || this.sellCoinGeckoId;
    }

    // Trade types - prefer the primary asset based on side
    if (this.isTradeType()) {
      return this.side === 'buy'
        ? (this.buyCoinGeckoId || this.sellCoinGeckoId)
        : (this.sellCoinGeckoId || this.buyCoinGeckoId);
    }

    // Send/Receive/Withdrawal - use sell asset
    if (this.isBlockchainTransferType() || this.isWithdrawal()) {
      return this.sellCoinGeckoId || this.buyCoinGeckoId;
    }

    // Deposit/Interest/Staking - use buy asset
    if (this.isDeposit() || this.isInterestType()) {
      return this.buyCoinGeckoId || this.sellCoinGeckoId;
    }

    // Fallback: try any available ID
    return this.buyCoinGeckoId || this.sellCoinGeckoId;
  }
}
